import { observer } from 'mobx-react';
import { Table, Input, Button, Space, Typography, Alert, Tooltip } from 'antd';
import { GlobalOutlined, ReloadOutlined, RightOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { useStore } from '@/stores';
import type { Merchant } from '@/api/types';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill } from '@/components/StatusPill';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { HelpHotspot } from '@/features/help/HelpHotspot';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';
import { switchPractice } from '@/components/practiceSwitch';

const { Text } = Typography;

// Platform-admin view: all merchants in the system. Switching to a merchant
// from here updates MerchantContextStore (so the workspace section in the
// sidebar reflects the new context immediately).

export const AllMerchantsPage = observer(function AllMerchantsPage() {
  const { merchantContextStore: ctx } = useStore();
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (ctx.merchants.length === 0 && !ctx.isLoading) void ctx.loadMerchants();
  }, [ctx]);

  const filtered = search.trim()
    ? ctx.merchants.filter((m) =>
        [m.title, m.codeName, m.city, m.province, m.zipCode]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(search.toLowerCase()))
      )
    : ctx.merchants;

  function selectAndOpenWorkspace(m: Merchant): void {
    void switchPractice(ctx, m.id, () => trackEvent('Merchant.SwitchedFromAdmin', { merchantId: m.id }));
  }

  const columns: ColumnsType<Merchant> = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      sorter: (a, b) => (a.title ?? '').localeCompare(b.title ?? ''),
      render: (t: string, row) => (
        <div>
          <Text style={{ fontWeight: 600, fontSize: 13.5, color: colors.slate[900] }}>
            {t}
          </Text>
          {row.id === ctx.selectedMerchantId && (
            <span style={{ marginLeft: 8 }}>
              <StatusPill tone="green">Current</StatusPill>
            </span>
          )}
        </div>
      ),
    },
    {
      title: 'Code',
      dataIndex: 'codeName',
      key: 'codeName',
      width: 160,
      render: (c?: string) => (
        <Text style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: colors.slate[500] }}>
          {c ?? '—'}
        </Text>
      ),
    },
    {
      title: 'Location',
      key: 'location',
      width: 220,
      render: (_t, row) => {
        const parts = [row.city, row.province].filter(Boolean).join(', ');
        return parts ? <Text type="secondary" style={{ fontSize: 13 }}>{parts}</Text> : <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (v?: boolean) =>
        v === false ? <StatusPill tone="slate">Inactive</StatusPill> : <StatusPill tone="green">Active</StatusPill>,
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (v: number) => (
        <Text style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: colors.slate[500] }}>
          #{v}
        </Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      align: 'right',
      fixed: 'right',
      onCell: () => ({ onClick: (e) => e.stopPropagation() }),
      render: (_t, row) => (
        <Tooltip title="Switch context to this merchant">
          <Button
            type="primary"
            ghost
            size="small"
            icon={<RightOutlined />}
            onClick={() => selectAndOpenWorkspace(row)}
          >
            {row.id === ctx.selectedMerchantId ? 'Active' : 'Open'}
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHeader
        accent="violet"
        icon={<GlobalOutlined />}
        title={
          <Space size={6} align="center">
            <span>All merchants</span>
            <HelpHotspot topic="merchants.overview" />
          </Space>
        }
        subtitle="Cross-tenant view. Click Open on any row to switch your workspace context."
        count={ctx.merchants.length}
        countLabel={ctx.merchants.length === 1 ? 'merchant' : 'merchants'}
        actions={
          <Space>
            <Input.Search
              placeholder="Search merchants…"
              allowClear
              style={{ width: 280 }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button icon={<ReloadOutlined />} loading={ctx.isLoading} onClick={() => void ctx.loadMerchants()}>
              Refresh
            </Button>
          </Space>
        }
      />

      {ctx.error && (
        <Alert type="error" message="Failed to load merchants" description={ctx.error} showIcon closable />
      )}

      <SectionCard noPadding>
        <Table<Merchant>
          rowKey="id"
          columns={columns}
          dataSource={filtered}
          loading={ctx.isLoading}
          pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} merchants`, style: { padding: '12px 18px', margin: 0 } }}
          locale={{
            emptyText: (
              <BrandedEmpty
                icon={<GlobalOutlined />}
                title={search ? `No merchants match "${search}"` : 'No merchants accessible'}
                description={search ? 'Try a different search term.' : 'You may not have admin permissions on any merchants.'}
              />
            ),
          }}
        />
      </SectionCard>
    </div>
  );
});
